import React, { useState, useEffect, useCallback } from "react";

export default function TemplatePreviewModal({ template, onUseTemplate, onClose }) {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaSrc, setMediaSrc] = useState(null);
  const [isVideo, setIsVideo] = useState(false);

  // Build the media URL from template data
  useEffect(() => {
    if (template) {
      const { category, filename, templateId, firebaseUrl, source, isVideo: templateIsVideo } = template;
      const baseUrl = process.env.REACT_APP_API_URL || "http://192.168.1.165:8000";
      
      // If we have the full filename, use it; otherwise construct it
      const file = filename || `${category.toLowerCase()}${templateId || 1}.png`;
      
      // Check if this is a video file
      const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
      const isVideoFile = templateIsVideo || videoExtensions.some(ext => file.toLowerCase().endsWith(ext));
      setIsVideo(isVideoFile);
      
      // Use Firebase URL if available (for videos), otherwise use local API
      if (firebaseUrl && source === 'firebase') {
        setMediaSrc(firebaseUrl);
      } else {
        setMediaSrc(`${baseUrl}/templates/${category}/${file}`);
      }
    }
  }, [template]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Handle media ready - for cross-browser support
  const handleMediaReady = useCallback(() => {
    if (!mediaLoaded) {
      setMediaLoaded(true);
    }
  }, [mediaLoaded]);

  if (!template || !mediaSrc) return null;

  const categoryName = template.category.charAt(0).toUpperCase() + template.category.slice(1);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-[320px] sm:max-w-sm overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Template media (image or video) - constrained height */}
        <div className={`relative flex-shrink-0 ${isVideo ? 'bg-gray-900' : 'bg-gray-100'}`} style={{ maxHeight: '45vh' }}>
          {!mediaLoaded && (
            <div className="absolute inset-0 flex items-center justify-center min-h-[200px]">
              <div className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-indigo-500 animate-spin" />
            </div>
          )}
          
          {isVideo ? (
            <>
              {/* Video preview - Safari optimized */}
              <video
                src={`${mediaSrc}#t=0.001`}
                autoPlay
                loop
                muted
                playsInline
                webkit-playsinline="true"
                preload="auto"
                crossOrigin="anonymous"
                className={`w-full h-full object-contain transition-opacity duration-300 ${mediaLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ maxHeight: '45vh' }}
                onCanPlay={handleMediaReady}
                onLoadedData={handleMediaReady}
              />
              {/* Video indicator badge */}
              <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-10">
                <svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                <span>Video</span>
              </div>
            </>
          ) : (
            <img
              src={mediaSrc}
              alt={`${categoryName} template`}
              className={`w-full h-full object-contain transition-opacity duration-300 ${mediaLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ maxHeight: '45vh' }}
              onLoad={handleMediaReady}
            />
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 flex-shrink-0">
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              {categoryName}
            </span>
            <span className="text-gray-400 text-xs">{isVideo ? 'Video Template' : 'Template'}</span>
          </div>

          {/* Title */}
          <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
            {categoryName} {isVideo ? 'Video' : 'Card'}
          </h2>
          
          <p className="text-gray-500 text-xs sm:text-sm mb-4 line-clamp-2">
            {isVideo 
              ? 'Add your personalized message to this animated video card!'
              : 'Customize with your message and photos. Perfect for any occasion!'}
          </p>

          {/* CTA Button */}
          <button
            onClick={() => onUseTemplate(template)}
            className="w-full py-2.5 sm:py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Use This Template
          </button>

          {/* Secondary info */}
          <p className="mt-2.5 text-center text-[10px] sm:text-xs text-gray-400">
            Start your free trial today
          </p>
        </div>
      </div>
    </div>
  );
}
